import React, { useEffect, useMemo, useState } from 'react';
import { Building2, KeyRound, LockKeyhole, Plus, Save, ShieldCheck, Trash2, UserCog, Users } from 'lucide-react';
import { api } from '../api.js';
import { Avatar, Badge, Button, Callout, PageHeader, SectionHeader } from '../components/ui.jsx';
import { classNames } from '../utils.js';

const EMPTY_USER = {
  name: '', email: '', role: 'hiring_manager', title: '', active: true, departmentIds: [],
  temporaryPassword: '', mustChangePassword: true, passwordConfigured: false,
};

function normalizeUser(user) {
  return user ? { ...EMPTY_USER, ...user, departmentIds: [...(user.departmentIds || [])] } : { ...EMPTY_USER };
}

export default function AccountsPage({ state, refresh, notify }) {
  const users = state?.users || [];
  const departments = state?.departments || [];
  const roleLabels = state?.workflow?.roleLabels || {};
  const actorId = state?.currentUser?.id;
  const [editingId, setEditingId] = useState(users[0]?.id || 'new');
  const [draft, setDraft] = useState(() => normalizeUser(users[0]));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const editingVersion = users.find((user) => user.id === editingId)?.updatedAt || '';
  useEffect(() => {
    if (editingId === 'new') return;
    const user = users.find((item) => item.id === editingId);
    if (user) setDraft(normalizeUser(user));
  }, [editingId, editingVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const canAssignResponsibilities = draft.role === 'hiring_manager';
  const assignedDepartments = useMemo(() => new Set(canAssignResponsibilities
    ? draft.departmentIds || [] : departments.map((item) => item.id)), [canAssignResponsibilities, draft.departmentIds, departments]);
  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const toggleDepartment = (departmentId) => update('departmentIds', assignedDepartments.has(departmentId)
    ? draft.departmentIds.filter((id) => id !== departmentId)
    : [...draft.departmentIds, departmentId]);

  const save = async () => {
    if (!draft.name.trim() || !draft.email.trim()) return notify('请填写姓名和邮箱', 'danger');
    if (editingId === 'new' && !draft.temporaryPassword) return notify('请为新账户设置临时密码', 'danger');
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        email: draft.email,
        role: draft.role,
        title: draft.title,
        active: draft.active,
        actorId,
        ...(editingId === 'new' ? {
          password: draft.temporaryPassword,
          mustChangePassword: draft.mustChangePassword,
        } : {}),
      };
      const saved = editingId === 'new'
        ? await api.createUser(payload)
        : await api.updateUser(editingId, payload);
      if (editingId !== 'new' && draft.temporaryPassword) {
        await api.resetUserPassword(saved.id, {
          actorId,
          password: draft.temporaryPassword,
          mustChangePassword: draft.mustChangePassword,
        });
      }
      await api.assignUserDepartments(saved.id, {
        actorId, departmentIds: canAssignResponsibilities ? draft.departmentIds : [],
      });
      setEditingId(saved.id);
      notify(editingId === 'new' ? '账户已创建并完成部门授权' : '账户与部门权限已保存');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    const user = users.find((item) => item.id === editingId);
    if (!user || user.id === actorId) return;
    if (!window.confirm(`确定删除账户“${user.name}”吗？该账户的登录会话和部门授权会立即撤销。`)) return;
    setDeleting(true);
    try {
      await api.deleteUser(user.id);
      const next = users.find((item) => item.id !== user.id);
      setEditingId(next?.id || 'new');
      setDraft(normalizeUser(next));
      notify('账户已删除');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="page accounts-page">
      <PageHeader
        eyebrow="组织与权限"
        title="账户与部门权限"
        description="招聘官只能查看和管理已授权负责部门的招聘数据；人事负责全组织招聘运营"
        actions={<Button variant="primary" icon={Plus} onClick={() => { setEditingId('new'); setDraft(normalizeUser()); }}>新建账户</Button>}
      />

      <div className="accounts-layout">
        <aside className="account-list-panel">
          <div className="account-list-head"><strong>团队账户</strong><Badge>{users.length}</Badge></div>
          <div className="account-list">{users.map((user) => (
            <button key={user.id} className={classNames(editingId === user.id && 'active')} onClick={() => setEditingId(user.id)}>
              <Avatar name={user.name} size="md" />
              <span><strong>{user.name}</strong><small>{roleLabels[user.role]} · {user.title || '未设置职务'}</small></span>
              <Badge tone={user.active ? 'success' : 'neutral'}>{user.active ? '启用' : '停用'}</Badge>
            </button>
          ))}</div>
        </aside>

        <section className="account-editor panel">
          <header>
            <div><span>{editingId === 'new' ? '新账户' : '账户设置'}</span><h2>{draft.name || '未命名账户'}</h2></div>
            <div className="account-editor-actions">
              {editingId !== 'new' && editingId !== actorId && <Button variant="danger-ghost" icon={Trash2} loading={deleting} onClick={remove}>删除账户</Button>}
              <Button variant="primary" icon={Save} loading={saving} onClick={save}>保存</Button>
            </div>
          </header>
          <div className="account-editor-body">
            <section>
              <SectionHeader title="基本信息" description="邮箱用于招聘通知和 Teams 日历邀请" />
              <div className="form-grid two">
                <label><span>姓名</span><input value={draft.name} onChange={(event) => update('name', event.target.value)} /></label>
                <label><span>工作邮箱</span><input type="email" value={draft.email} onChange={(event) => update('email', event.target.value)} /></label>
                <label><span>角色</span><select value={draft.role} onChange={(event) => update('role', event.target.value)}><option value="hiring_manager">招聘官</option><option value="hr">人事</option><option value="admin">管理员</option></select></label>
                <label><span>职务</span><input value={draft.title} onChange={(event) => update('title', event.target.value)} /></label>
              </div>
              <label className="check-row account-active-check"><input type="checkbox" checked={draft.active} onChange={(event) => update('active', event.target.checked)} />允许该账户进入招聘工作台</label>
            </section>

            <section className="account-password-section">
              <SectionHeader
                title="密码与登录安全"
                description={editingId === 'new' ? '为新账户设置临时密码' : '管理员可以重置其他账户的密码并撤销其已有会话'}
                action={<Badge tone={draft.passwordConfigured ? 'success' : 'warning'}>{draft.passwordConfigured ? '密码已设置' : '尚未设置密码'}</Badge>}
              />
              {editingId === actorId ? <Callout tone="info" title="当前登录账户">请使用页面右上角的“修改密码”入口更新自己的密码；管理员不能在这里绕过当前密码验证。</Callout> : <>
                <label className="account-password-field"><span>{editingId === 'new' ? '临时密码' : '新临时密码（留空则不修改）'}</span><div className="input-with-icon"><KeyRound size={16} /><input type="password" autoComplete="new-password" value={draft.temporaryPassword || ''} onChange={(event) => update('temporaryPassword', event.target.value)} placeholder="至少 10 个字符，包含字母和数字" /></div></label>
                <label className="check-row account-active-check"><input type="checkbox" checked={Boolean(draft.mustChangePassword)} onChange={(event) => update('mustChangePassword', event.target.checked)} />首次登录后必须修改密码</label>
                <div className="account-session-note"><LockKeyhole size={16} /><span>重置密码后，该账户在其他设备上的登录会话会立即失效。</span></div>
              </>}
            </section>

            <section className="account-job-section">
              <SectionHeader
                title={canAssignResponsibilities ? '主要负责部门' : '部门数据范围'}
                description={canAssignResponsibilities
                  ? '用于控制该招聘官可查看和管理的岗位、候选人与简历范围'
                  : '该角色固定拥有全部部门的数据范围'}
              />
              <div className="account-job-grid">{departments.map((department) => (
                <label key={department.id} className={classNames(assignedDepartments.has(department.id) && 'selected', !canAssignResponsibilities && 'disabled')}>
                  <input type="checkbox" disabled={!canAssignResponsibilities} checked={assignedDepartments.has(department.id)} onChange={() => toggleDepartment(department.id)} />
                  <Building2 size={17} />
                  <span><strong>{department.name}</strong><small>{department.jobCount || 0} 个岗位 · {department.userCount || 0} 位授权成员</small></span>
                </label>
              ))}</div>
            </section>

            <section className="permission-summary">
              <div className={draft.role === 'hiring_manager' ? 'active' : ''}><UserCog size={19} /><span><strong>招聘官</strong><small>已授权部门内的岗位管理、简历导入、AI 评估、人工复核与面试反馈</small></span></div>
              <div className={draft.role === 'hr' ? 'active' : ''}><Users size={19} /><span><strong>人事</strong><small>全组织岗位管理、简历导入、AI 评估、约面与 Offer 推进</small></span></div>
              <div className={draft.role === 'admin' ? 'active' : ''}><ShieldCheck size={19} /><span><strong>管理员</strong><small>账户、岗位、模型、审计和异常恢复</small></span></div>
            </section>

            <Callout tone="warning" title="Microsoft Graph 尚未配置" action={<Badge tone="warning">通知任务暂存</Badge>}>
              招聘通知和 Teams 日历任务会进入可审计队列，配置组织 Graph 凭据后再由后台发送。
            </Callout>
          </div>
        </section>
      </div>
    </div>
  );
}
